import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

type TokenProvider = () => string | null;
type RefreshHandler = () => Promise<string | null>;

export interface ApiClientOptions {
  getToken: TokenProvider;
  onUnauthorizedRefresh?: RefreshHandler;
  enableLogging?: boolean;
  baseURL?: string;
}

export class ApiClient {
  private instance: AxiosInstance;
  private getToken: TokenProvider;
  private onUnauthorizedRefresh?: RefreshHandler;
  private enableLogging: boolean;

  constructor(options: ApiClientOptions) {
    this.getToken = options.getToken;
    this.onUnauthorizedRefresh = options.onUnauthorizedRefresh;
    this.enableLogging = Boolean(options.enableLogging);

    this.instance = axios.create({
      baseURL: options.baseURL || process.env.VUE_APP_API_PREFIX,
      headers: { 'Content-Type': 'application/json' },
    });

    this.instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = this.getToken();
      if (token) {
        config.headers = config.headers || {};
        (config.headers as any)['Authorization'] = `Bearer ${token}`;
      }
      if (this.enableLogging) {
        // eslint-disable-next-line no-console
        console.debug('[ApiClient] Request', config.method, config.url, config.params || config.data);
      }
      return config;
    });

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        if (this.enableLogging) {
          // eslint-disable-next-line no-console
          console.debug('[ApiClient] Response', response.status, response.config.url);
        }
        return response;
      },
      async (error) => {
        const original = error.config;
        const status = error?.response?.status;
        if (status === 401 && this.onUnauthorizedRefresh && !original.__isRetry) {
          original.__isRetry = true;
          const newToken = await this.onUnauthorizedRefresh();
          if (newToken) {
            original.headers = original.headers || {};
            original.headers['Authorization'] = `Bearer ${newToken}`;
            return this.instance(original);
          }
        }
        return Promise.reject(this.normalizeError(error));
      }
    );
  }

  get axios(): AxiosInstance {
    return this.instance;
  }

  private normalizeError(error: any) {
    const status = error?.response?.status || 0;
    const message = error?.response?.data?.error || error?.message || 'Unknown error';
    return { status, message, raw: error };
  }
}


